import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import { rupee, waLink } from "@/lib/whatsapp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { MessageCircle, Loader2, Lock } from "lucide-react";

export default function Checkout() {
  const { cart, subtotal, clearCart, user, settings } = useApp();
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState("");
  const [coupon, setCoupon] = useState(null);
  const pay = settings.payment || {};
  const onlineAvailable = !!pay.online_enabled;
  const codAvailable = pay.cod_enabled !== false;
  const [payMethod, setPayMethod] = useState("cod");
  const [form, setForm] = useState({
    full_name: user?.name || "", phone: user?.phone || "", email: user?.email || "",
    line1: "", line2: "", city: "", state: "", pincode: "",
  });

  useEffect(() => {
    // default to online if it's the only option, else COD
    if (onlineAvailable && !codAvailable) setPayMethod("online");
    else setPayMethod(codAvailable ? "cod" : "online");
  }, [onlineAvailable, codAvailable]);

  useEffect(() => {
    if (!user) return;
    api.get("/me/addresses").then((r) => {
      const list = r.data || [];
      const def = list.find((a) => a.is_default) || list[0];
      if (def) {
        setForm((f) => ({
          ...f,
          full_name: def.name || f.full_name,
          phone: def.phone || f.phone,
          line1: [def.flat, def.street].filter(Boolean).join(", ") || f.line1,
          line2: def.area || f.line2,
          city: def.city || f.city,
          state: def.state || f.state,
          pincode: def.pincode || f.pincode,
        }));
      }
    }).catch(() => {});
  }, [user]);

  const discount = coupon?.discount || 0;
  const delivery = subtotal >= 500 ? 0 : subtotal > 0 ? 40 : 0;
  const tax = Math.round(Math.max(subtotal - discount, 0) * 0.05);
  const total = Math.round(Math.max(subtotal - discount, 0) + tax + delivery);

  const placedRef = useRef(false);
  useEffect(() => { if (cart.length === 0 && !placedRef.current) nav("/cart"); }, [cart.length, nav]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const cartEmpty = cart.length === 0;

  const applyCoupon = () => {
    if (!code.trim()) return;
    api.post("/coupons/validate", { code: code.trim(), subtotal }).then((r) => { setCoupon(r.data); toast.success("Coupon applied"); })
      .catch((e) => toast.error(e.response?.data?.detail || "Invalid coupon"));
  };

  const placeOrder = () => {
    for (const k of ["full_name", "phone", "line1", "city", "state", "pincode"]) {
      if (!form[k].trim()) return toast.error("Please fill all required fields");
    }
    setLoading(true);
    const payload = {
      items: cart.map((i) => ({ product_id: i.product_id, name: i.name, price: i.price, mrp: i.mrp, qty: i.qty, image: i.image, sku: i.sku, pack_size: i.pack_size })),
      address: { full_name: form.full_name, phone: form.phone, line1: form.line1, line2: form.line2, city: form.city, state: form.state, pincode: form.pincode },
      email: form.email, coupon_code: coupon?.code || "",
    };
    if (payMethod === "online") {
      api.post("/payments/checkout", { ...payload, payment_method: "Online", origin_url: window.location.origin })
        .then((r) => { window.location.href = r.data.checkout_url; })
        .catch((e) => { toast.error(e.response?.data?.detail || "Could not start payment"); setLoading(false); });
      return;
    }
    api.post("/orders", { ...payload, payment_method: "COD" })
      .then((r) => { placedRef.current = true; clearCart(); nav(`/order-confirmation/${r.data.id}`); })
      .catch((e) => toast.error(e.response?.data?.detail || "Could not place order"))
      .finally(() => setLoading(false));
  };

  if (cartEmpty) return null;

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8 lg:py-12">
      <h1 className="font-heading text-3xl lg:text-4xl text-forest tracking-tight mb-8">Checkout</h1>
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-2xl soft-shadow p-6">
            <h3 className="font-heading text-2xl text-forest mb-4">Customer & Delivery Details</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Full Name *"><Input value={form.full_name} onChange={set("full_name")} data-testid="checkout-name" className="rounded-xl bg-sand border-sage/40" /></Field>
              <Field label="Phone *"><Input value={form.phone} onChange={set("phone")} data-testid="checkout-phone" className="rounded-xl bg-sand border-sage/40" /></Field>
              <Field label="Email" full><Input value={form.email} onChange={set("email")} data-testid="checkout-email" className="rounded-xl bg-sand border-sage/40" /></Field>
              <Field label="Address Line 1 *" full><Input value={form.line1} onChange={set("line1")} data-testid="checkout-line1" className="rounded-xl bg-sand border-sage/40" /></Field>
              <Field label="Address Line 2" full><Input value={form.line2} onChange={set("line2")} className="rounded-xl bg-sand border-sage/40" /></Field>
              <Field label="City *"><Input value={form.city} onChange={set("city")} data-testid="checkout-city" className="rounded-xl bg-sand border-sage/40" /></Field>
              <Field label="State *"><Input value={form.state} onChange={set("state")} data-testid="checkout-state" className="rounded-xl bg-sand border-sage/40" /></Field>
              <Field label="Pincode *"><Input value={form.pincode} onChange={set("pincode")} data-testid="checkout-pincode" className="rounded-xl bg-sand border-sage/40" /></Field>
            </div>
          </div>
          <div className="bg-white rounded-2xl soft-shadow p-6">
            <h3 className="font-heading text-2xl text-forest mb-3">Payment Method</h3>
            <div className="space-y-3">
              {onlineAvailable && (
                <button type="button" onClick={() => setPayMethod("online")} data-testid="pay-method-online"
                  className={`w-full text-left border-2 rounded-2xl p-4 flex items-center gap-3 transition-colors ${payMethod === "online" ? "border-forest bg-sand/50" : "border-sage/30 hover:border-sage"}`}>
                  <span className={`w-4 h-4 rounded-full shrink-0 ${payMethod === "online" ? "bg-forest" : "border-2 border-sage"}`} />
                  <div><div className="font-500 text-ink">Pay Online (Card / UPI / Netbanking)</div><div className="text-xs text-muted-foreground">Secure payment via {(pay.provider || "gateway")}. You'll be redirected to complete payment.</div></div>
                </button>
              )}
              {codAvailable && (
                <button type="button" onClick={() => setPayMethod("cod")} data-testid="pay-method-cod"
                  className={`w-full text-left border-2 rounded-2xl p-4 flex items-center gap-3 transition-colors ${payMethod === "cod" ? "border-forest bg-sand/50" : "border-sage/30 hover:border-sage"}`}>
                  <span className={`w-4 h-4 rounded-full shrink-0 ${payMethod === "cod" ? "bg-forest" : "border-2 border-sage"}`} />
                  <div><div className="font-500 text-ink">Cash on Delivery</div><div className="text-xs text-muted-foreground">Pay when your order arrives.</div></div>
                </button>
              )}
              {!onlineAvailable && !codAvailable && <p className="text-sm text-terracotta">No payment method is currently enabled. Please contact us on WhatsApp.</p>}
            </div>
            <a href={waLink(settings.whatsapp_number, "Hi LIVENEAT Organic, I need help with my checkout.")} target="_blank" rel="noreferrer" className="text-sm text-[#25D366] font-500 inline-flex items-center gap-1.5 mt-4" data-testid="checkout-wa-help"><MessageCircle className="w-4 h-4" /> Need help? Chat on WhatsApp</a>
          </div>
        </div>

        <div>
          <div className="bg-white rounded-2xl soft-shadow p-6 sticky top-28">
            <h3 className="font-heading text-2xl text-forest mb-4">Order Summary</h3>
            <div className="space-y-3 max-h-52 overflow-auto hide-scrollbar mb-4">
              {cart.map((i) => (
                <div key={i.product_id} className="flex gap-3 items-center">
                  <img src={i.image} alt="" className="w-12 h-12 rounded-lg object-cover bg-sand" />
                  <div className="flex-1 min-w-0"><div className="text-sm text-ink truncate">{i.name}</div><div className="text-xs text-muted-foreground">Qty {i.qty}</div></div>
                  <span className="text-sm font-500">{rupee(i.price * i.qty)}</span>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mb-4">
              <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Discount code" className="rounded-full bg-sand border-sage/40" data-testid="checkout-coupon-input" />
              <Button onClick={applyCoupon} variant="outline" className="rounded-full border-forest text-forest shrink-0">Apply</Button>
            </div>
            <div className="space-y-2 text-sm border-t border-sage/20 pt-4">
              <div className="flex justify-between"><span className="text-brown/70">Subtotal</span><span>{rupee(subtotal)}</span></div>
              {discount > 0 && <div className="flex justify-between"><span className="text-brown/70">Discount</span><span className="text-terracotta">- {rupee(discount)}</span></div>}
              <div className="flex justify-between"><span className="text-brown/70">Tax (5%)</span><span>{rupee(tax)}</span></div>
              <div className="flex justify-between"><span className="text-brown/70">Delivery</span><span>{delivery === 0 ? "FREE" : rupee(delivery)}</span></div>
              <div className="flex justify-between border-t border-sage/20 pt-3 mt-2"><span className="font-600 text-lg text-forest">Total</span><span className="font-600 text-lg text-forest" data-testid="checkout-total">{rupee(total)}</span></div>
            </div>
            <Button onClick={placeOrder} disabled={loading} className="w-full mt-5 rounded-full h-12 bg-forest hover:bg-earth text-cream" data-testid="place-order-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Lock className="w-4 h-4 mr-1.5" /> {payMethod === "online" ? "Pay Now" : "Place Order"}</>}
            </Button>
            {!user && <p className="text-xs text-center text-muted-foreground mt-3">Checking out as guest. <Link to="/login" className="text-forest underline">Log in</Link> to track orders.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, full }) {
  return <div className={full ? "sm:col-span-2" : ""}><Label className="text-sm text-brown mb-1.5 block">{label}</Label>{children}</div>;
}
